import React, { useState, useEffect } from 'react';
import { uploadMediaFile } from '../../utils/imageUploadHelper';
import { resolveImageUrl, slugify } from '../../utils/helpers';
import { addUnit } from '../../utils/styleHelpers';
import MediaLibraryModal from '../../components/MediaLibraryModal';
import { LuPenLine, LuX, LuPackage, LuFileText, LuImage, LuSlidersHorizontal, LuLink, LuUpload, LuTrash2, LuPlus, LuSave } from 'react-icons/lu';

export const ProductExpandedEditModal = ({
    isOpen,
    onClose,
    product,
    categories = [],
    onSave,
    primaryColor = '#3b82f6'
}) => {
    const [localProduct, setLocalProduct] = useState(null);
    const [activeTab, setActiveTab] = useState('general');
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingSpecSheet, setUploadingSpecSheet] = useState(false);
    const [newImageUrl, setNewImageUrl] = useState('');
    const [newAttrName, setNewAttrName] = useState('');
    const [newAttrOptions, setNewAttrOptions] = useState('');
    const [variationImgUploadingId, setVariationImgUploadingId] = useState(null);
    const [mediaLibraryTarget, setMediaLibraryTarget] = useState(null); // null | 'product_gallery' | 'spec_sheet' | `var_${id}`

    useEffect(() => {
        if (product) {
            const rawImages = (product.images && product.images.length > 0)
                ? [...product.images]
                : (product.image ? [product.image] : ['./public/hero_abstract_bg.png']);

            const combinedAttrs = {
                ...(product.pageConfig?.attributes || {}),
                ...(product.attributes || {})
            };

            const combinedVars = (Array.isArray(product.variations) && product.variations.length > 0)
                ? JSON.parse(JSON.stringify(product.variations))
                : (Array.isArray(product.pageConfig?.variations) ? JSON.parse(JSON.stringify(product.pageConfig.variations)) : []);

            setLocalProduct({
                ...product,
                images: rawImages,
                attributes: combinedAttrs,
                variations: combinedVars,
                specSheetUrl: product.specSheetUrl || product.pageConfig?.specSheetUrl || '',
                shortDescription: product.shortDescription !== undefined ? product.shortDescription : (product.description || ''),
                description: product.description || '',
                price: product.price !== undefined ? product.price : 0,
                regularPrice: product.regularPrice !== undefined ? product.regularPrice : (product.price || 0),
                salePrice: product.salePrice !== undefined ? product.salePrice : '',
                sku: product.sku || product.pageConfig?.sku || '',
                slug: product.slug || product.pageConfig?.slug || slugify(product.name || ''),
                stock: product.stock !== undefined ? product.stock : 1,
                tags: Array.isArray(product.tags) ? product.tags : (typeof product.tags === 'string' ? product.tags.split(',').map(t => t.trim()).filter(Boolean) : (Array.isArray(product.pageConfig?.tags) ? product.pageConfig.tags : [])),
                hasBottleDeposit: product.hasBottleDeposit !== undefined ? Boolean(product.hasBottleDeposit) : Boolean(product.pageConfig?.hasBottleDeposit),
                bottleCount: (product.bottleCount !== undefined && product.bottleCount !== null) ? product.bottleCount : ((product.pageConfig?.bottleCount !== undefined) ? product.pageConfig.bottleCount : 1),
                bottleDepositFee: product.bottleDepositFee !== undefined ? product.bottleDepositFee : (product.pageConfig?.bottleDepositFee || ''),
                inquiryOnly: Boolean(product.inquiryOnly),
                status: (product.hidden || product.status === 'draft' || product.status === 'hidden') ? 'draft' : 'published',
                hidden: Boolean(product.hidden || product.status === 'draft' || product.status === 'hidden'),
                pageConfig: {
                    ...(product.pageConfig || {}),
                    status: (product.hidden || product.status === 'draft' || product.status === 'hidden') ? 'draft' : 'published',
                    hidden: Boolean(product.hidden || product.status === 'draft' || product.status === 'hidden'),
                    slug: product.slug || product.pageConfig?.slug || slugify(product.name || ''),
                    attributes: combinedAttrs,
                    variations: combinedVars,
                    specSheetUrl: product.specSheetUrl || product.pageConfig?.specSheetUrl || ''
                }
            });
            setActiveTab('general');
        }
    }, [product, isOpen]);

    if (!isOpen || !localProduct) return null;

    const updateField = (field, value) => {
        setLocalProduct(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                [field]: value,
                pageConfig: {
                    ...(prev.pageConfig || {}),
                    [field]: value
                }
            };
        });
    };

    // Images handling
    const handleAddImageUrl = () => {
        if (!newImageUrl.trim()) return;
        const updatedImgs = [...(localProduct.images || []), newImageUrl.trim()];
        updateField('images', updatedImgs);
        setNewImageUrl('');
    };

    const handleRemoveImage = (imgIdx) => {
        const updatedImgs = (localProduct.images || []).filter((_, i) => i !== imgIdx);
        updateField('images', updatedImgs.length > 0 ? updatedImgs : ['./public/hero_abstract_bg.png']);
    };

    const handleSetPrimaryImage = (imgIdx) => {
        const currentImgs = [...(localProduct.images || [])];
        if (imgIdx <= 0 || imgIdx >= currentImgs.length) return;
        const [moved] = currentImgs.splice(imgIdx, 1);
        currentImgs.unshift(moved);
        updateField('images', currentImgs);
    };

    const handleImageFileUpload = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setUploadingImage(true);
        try {
            const res = await uploadMediaFile(file);
            if (res && res.url) {
                let cleanUrl = res.url;
                if (cleanUrl.startsWith('./')) cleanUrl = cleanUrl.substring(1);
                if (!cleanUrl.startsWith('/') && !cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://') && !cleanUrl.startsWith('data:')) {
                    cleanUrl = '/' + cleanUrl;
                }
                const updatedImgs = [...(localProduct.images || []), cleanUrl];
                updateField('images', updatedImgs);
            } else {
                alert('Upload failed: ' + (res?.serverError || 'Unknown error'));
            }
        } catch (err) {
            console.error('Image upload error:', err);
            alert('Error uploading image.');
        } finally {
            setUploadingImage(false);
            if (e.target) e.target.value = '';
        }
    };

    // Spec Sheet Upload
    const handleSpecSheetUpload = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setUploadingSpecSheet(true);
        try {
            const res = await uploadMediaFile(file);
            if (res && res.url) {
                let cleanUrl = res.url;
                if (cleanUrl.startsWith('./')) cleanUrl = cleanUrl.substring(1);
                if (!cleanUrl.startsWith('/') && !cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://') && !cleanUrl.startsWith('data:')) {
                    cleanUrl = '/' + cleanUrl;
                }
                updateField('specSheetUrl', cleanUrl);
            } else {
                alert('Spec sheet upload failed: ' + (res?.serverError || 'Unknown error'));
            }
        } catch (err) {
            console.error('Spec sheet upload error:', err);
            alert('Error uploading spec sheet file.');
        } finally {
            setUploadingSpecSheet(false);
            if (e.target) e.target.value = '';
        }
    };

    const handleMediaLibrarySelect = (selectedUrl) => {
        if (!selectedUrl) return;
        let cleanUrl = selectedUrl;
        if (cleanUrl.startsWith('./')) cleanUrl = cleanUrl.substring(1);
        if (!cleanUrl.startsWith('/') && !cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://') && !cleanUrl.startsWith('data:')) {
            cleanUrl = '/' + cleanUrl;
        }

        if (mediaLibraryTarget === 'product_gallery') {
            const updatedImgs = [...(localProduct.images || []), cleanUrl];
            updateField('images', updatedImgs);
        } else if (mediaLibraryTarget === 'spec_sheet') {
            updateField('specSheetUrl', cleanUrl);
        } else if (mediaLibraryTarget && mediaLibraryTarget.startsWith('var_')) {
            const varId = mediaLibraryTarget.replace('var_', '');
            handleUpdateVariationField(varId, 'image', cleanUrl);
        }
        setMediaLibraryTarget(null);
    };

    // Attributes handling
    const handleAddAttribute = () => {
        if (!newAttrName.trim() || !newAttrOptions.trim()) return;
        const optionsList = newAttrOptions.split(',').map(s => s.trim()).filter(Boolean);
        if (optionsList.length === 0) return;

        const currentAttrs = { ...(localProduct.attributes || {}) };
        currentAttrs[newAttrName.trim()] = optionsList;

        setLocalProduct(prev => ({
            ...prev,
            attributes: currentAttrs,
            pageConfig: {
                ...(prev.pageConfig || {}),
                attributes: currentAttrs
            }
        }));

        setNewAttrName('');
        setNewAttrOptions('');
    };

    const handleUpdateAttributeOptions = (attrName, rawText) => {
        const optionsList = rawText.split(',').map(s => s.trim()).filter(Boolean);
        const currentAttrs = { ...(localProduct.attributes || {}) };
        currentAttrs[attrName] = optionsList;

        setLocalProduct(prev => ({
            ...prev,
            attributes: currentAttrs,
            pageConfig: {
                ...(prev.pageConfig || {}),
                attributes: currentAttrs
            }
        }));
    };

    const handleRemoveAttribute = (attrName) => {
        const currentAttrs = { ...(localProduct.attributes || {}) };
        delete currentAttrs[attrName];

        setLocalProduct(prev => ({
            ...prev,
            attributes: currentAttrs,
            pageConfig: {
                ...(prev.pageConfig || {}),
                attributes: currentAttrs
            }
        }));
    };

    // Variations handling
    const handleGenerateVariations = () => {
        const attrs = localProduct.attributes || {};
        const entries = Object.entries(attrs).filter(([_, opts]) => Array.isArray(opts) && opts.length > 0);
        if (entries.length === 0) {
            alert('Please add at least one attribute with options before generating variations.');
            return;
        }

        const cartesian = (arrays) => {
            return arrays.reduce((acc, curr) => {
                return acc.flatMap(a => curr.map(c => [...a, c]));
            }, [[]]);
        };

        const keys = entries.map(e => e[0]);
        const valueArrays = entries.map(e => e[1]);
        const combinations = cartesian(valueArrays);

        const newVariations = combinations.map((combo, idx) => {
            const varAttrs = {};
            keys.forEach((k, kIdx) => {
                varAttrs[k] = combo[kIdx];
            });

            const comboSkuSuffix = combo.map(c => slugify(String(c)).toUpperCase()).join('-');
            const varSku = (localProduct.sku ? `${localProduct.sku}-${comboSkuSuffix}` : comboSkuSuffix);

            return {
                id: `var_${Date.now()}_${idx}`,
                attributes: varAttrs,
                price: parseFloat(localProduct.price) || 0,
                regularPrice: parseFloat(localProduct.regularPrice) || parseFloat(localProduct.price) || 0,
                salePrice: localProduct.salePrice ? parseFloat(localProduct.salePrice) : null,
                sku: varSku,
                stock: parseInt(localProduct.stock, 10) || 999,
                image: (localProduct.images && localProduct.images[0]) || ''
            };
        });

        updateField('variations', newVariations);
    };

    const handleUpdateVariationField = (varId, field, val) => {
        const updated = (localProduct.variations || []).map(v => {
            if (v.id === varId) {
                return { ...v, [field]: val };
            }
            return v;
        });
        updateField('variations', updated);
    };

    const handleRemoveVariation = (varId) => {
        const updated = (localProduct.variations || []).filter(v => v.id !== varId);
        updateField('variations', updated);
    };

    const handleVariationImageUpload = async (varId, file) => {
        if (!file) return;
        setVariationImgUploadingId(varId);
        try {
            const res = await uploadMediaFile(file);
            if (res && res.url) {
                let cleanUrl = res.url;
                if (cleanUrl.startsWith('./')) cleanUrl = cleanUrl.substring(1);
                if (!cleanUrl.startsWith('/') && !cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://') && !cleanUrl.startsWith('data:')) {
                    cleanUrl = '/' + cleanUrl;
                }
                handleUpdateVariationField(varId, 'image', cleanUrl);
            } else {
                alert('Variation image upload failed.');
            }
        } catch (e) {
            console.error(e);
            alert('Error uploading variation image.');
        } finally {
            setVariationImgUploadingId(null);
        }
    };

    const handleSaveAndApply = () => {
        const finalSlug = (localProduct.slug || slugify(localProduct.name || '')).toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-_]/g, '');
        const isDraftOrHidden = Boolean(localProduct.hidden || localProduct.status === 'draft' || localProduct.status === 'hidden');
        const normalizedCategories = Array.isArray(localProduct.categories)
            ? localProduct.categories.map(c => typeof c === 'string' ? c : c?.name).filter(Boolean)
            : (localProduct.category ? [localProduct.category] : []);
        const primaryCategory = normalizedCategories[0] || '';
        const finalProd = {
            ...localProduct,
            status: isDraftOrHidden ? 'draft' : 'published',
            hidden: isDraftOrHidden,
            slug: finalSlug,
            price: parseFloat(localProduct.price) || 0,
            regularPrice: parseFloat(localProduct.regularPrice) || parseFloat(localProduct.price) || 0,
            salePrice: (localProduct.salePrice !== '' && localProduct.salePrice !== null && localProduct.salePrice !== undefined) ? parseFloat(localProduct.salePrice) : null,
            stock: (Array.isArray(localProduct.variations) && localProduct.variations.length > 0)
                ? localProduct.variations.reduce((sum, v) => sum + (parseInt(v.stock, 10) || 0), 0)
                : (parseInt(localProduct.stock, 10) || 0),
            specSheetUrl: localProduct.specSheetUrl || '',
            specSheetTitle: localProduct.specSheetTitle || '',
            customLinks: Array.isArray(localProduct.customLinks) ? localProduct.customLinks : [],
            images: localProduct.images && localProduct.images.length > 0 ? localProduct.images : ['./public/hero_abstract_bg.png'],
            categories: normalizedCategories,
            category: primaryCategory,
            tags: Array.isArray(localProduct.tags) ? localProduct.tags : [],
            pageConfig: {
                ...(localProduct.pageConfig || {}),
                status: isDraftOrHidden ? 'draft' : 'published',
                hidden: isDraftOrHidden,
                slug: finalSlug,
                tags: Array.isArray(localProduct.tags) ? localProduct.tags : [],
                attributes: localProduct.attributes || {},
                variations: localProduct.variations || [],
                specSheetUrl: localProduct.specSheetUrl || '',
                specSheetTitle: localProduct.specSheetTitle || '',
                customLinks: Array.isArray(localProduct.customLinks) ? localProduct.customLinks : []
            }
        };
        onSave(finalProd);
        onClose();
    };

    const topCategories = categories.filter(c => !c.parentId);
    const subCategories = localProduct.category
        ? categories.filter(c => {
            const parent = categories.find(p => p.id === c.parentId || p.name === c.parentId);
            return parent && (parent.name === localProduct.category || parent.id === localProduct.category);
        })
        : [];

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 999999,
                backgroundColor: 'rgba(15, 23, 42, 0.75)',
                backdropFilter: 'blur(6px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px',
                boxSizing: 'border-box'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '16px',
                    width: '920px',
                    maxWidth: '100%',
                    height: '85vh',
                    maxHeight: '850px',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                    fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Modal Header */}
                <div style={{
                    padding: '16px 24px',
                    backgroundColor: '#0f172a',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                    borderBottom: '1px solid #334155'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <LuPenLine size={22} style={{ color: '#38bdf8' }} />
                        <div>
                            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#ffffff' }}>
                                Edit Product: <span style={{ color: '#93c5fd' }}>{localProduct.name || 'Untitled Product'}</span>
                            </h3>
                            <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
                                Modify attributes, variations, media, pricing, and expanded product details
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            transition: 'all 0.15s ease'
                        }}
                    >
                        <LuX size={18} />
                    </button>
                </div>

                {/* Tab Navigation */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px 20px',
                    backgroundColor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    flexShrink: 0,
                    overflowX: 'auto'
                }}>
                    {[
                        { id: 'general', label: 'General & Pricing', icon: <LuPackage size={15} /> },
                        { id: 'descriptions', label: 'Descriptions', icon: <LuFileText size={15} /> },
                        { id: 'media', label: 'Gallery & Spec Sheet', icon: <LuImage size={15} /> },
                        { id: 'variations', label: 'Attributes & Variations', icon: <LuSlidersHorizontal size={15} /> }
                    ].map(t => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setActiveTab(t.id)}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '8px',
                                border: 'none',
                                background: activeTab === t.id ? '#2563eb' : 'transparent',
                                color: activeTab === t.id ? '#ffffff' : '#475569',
                                fontWeight: activeTab === t.id ? '700' : '600',
                                fontSize: '0.85rem',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                whiteSpace: 'nowrap',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            {t.icon}
                            <span>{t.label}</span>
                        </button>
                    ))}
                </div>

                {/* Tab Content Body */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '24px',
                    backgroundColor: '#ffffff',
                    boxSizing: 'border-box'
                }}>
                    {/* TAB 1: GENERAL & PRICING */}
                    {activeTab === 'general' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                        Product Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={localProduct.name || ''}
                                        onChange={e => updateField('name', e.target.value)}
                                        placeholder="e.g. Modern Brass Pendant Lamp"
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                        SKU / Part Number
                                    </label>
                                    <input
                                        type="text"
                                        value={localProduct.sku || ''}
                                        onChange={e => updateField('sku', e.target.value)}
                                        placeholder="e.g. LAMP-MOD-01"
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                        Categories <span style={{ fontWeight: '400', color: '#94a3b8' }}>(select all that apply)</span>
                                    </label>
                                    <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '8px 10px', maxHeight: '180px', overflowY: 'auto', background: '#fff', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        {topCategories.length === 0 && (
                                            <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No categories yet</span>
                                        )}
                                        {topCategories.map(cat => {
                                            const currentCats = Array.isArray(localProduct.categories) ? localProduct.categories : (localProduct.category ? [localProduct.category] : []);
                                            const isChecked = currentCats.some(c => (typeof c === 'string' ? c : c?.name) === cat.name);
                                            return (
                                                <label key={cat.id || cat.name} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.88rem', color: '#1e293b', padding: '2px 0' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={() => {
                                                            const currentCatsInner = Array.isArray(localProduct.categories) ? localProduct.categories.map(c => typeof c === 'string' ? c : c?.name).filter(Boolean) : (localProduct.category ? [localProduct.category] : []);
                                                            let newCats;
                                                            if (isChecked) {
                                                                newCats = currentCatsInner.filter(c => c !== cat.name);
                                                            } else {
                                                                newCats = [...currentCatsInner, cat.name];
                                                            }
                                                            const primaryCat = newCats[0] || '';
                                                            updateField('categories', newCats);
                                                            updateField('category', primaryCat);
                                                            if (primaryCat !== localProduct.category) {
                                                                updateField('subCategory', '');
                                                            }
                                                        }}
                                                        style={{ accentColor: cat.color || '#4f46e5', width: '15px', height: '15px', cursor: 'pointer', flexShrink: 0 }}
                                                    />
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                                        {cat.color && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: cat.color, flexShrink: 0, display: 'inline-block' }} />}
                                                        {cat.name}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                                {subCategories.length > 0 && (
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                            Subcategory <span style={{ fontWeight: '400', color: '#94a3b8' }}>(of primary)</span>
                                        </label>
                                        <select
                                            value={localProduct.subCategory || ''}
                                            onChange={e => updateField('subCategory', e.target.value)}
                                            style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.9rem', backgroundColor: '#fff', boxSizing: 'border-box' }}
                                        >
                                            <option value="">(None / General)</option>
                                            {subCategories.map(sub => (
                                                <option key={sub.id || sub.name} value={sub.name}>{sub.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                        Publish Status
                                    </label>
                                    <select
                                        value={(localProduct.hidden || localProduct.status === 'draft' || localProduct.status === 'hidden') ? 'draft' : 'published'}
                                        onChange={e => {
                                            const val = e.target.value;
                                            const isHidden = val === 'draft' || val === 'hidden';
                                            updateField('status', val);
                                            updateField('hidden', isHidden);
                                        }}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: `1.5px solid ${(localProduct.hidden || localProduct.status === 'draft' || localProduct.status === 'hidden') ? '#f59e0b' : '#10b981'}`,
                                            borderRadius: '8px',
                                            fontSize: '0.9rem',
                                            backgroundColor: (localProduct.hidden || localProduct.status === 'draft' || localProduct.status === 'hidden') ? '#fffbeb' : '#f0fdf4',
                                            color: (localProduct.hidden || localProduct.status === 'draft' || localProduct.status === 'hidden') ? '#b45309' : '#15803d',
                                            fontWeight: '700',
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        <option value="published">🟢 Published (Visible in Catalog)</option>
                                        <option value="draft">🟡 Draft / Unpublished (Hidden from Catalog)</option>
                                    </select>
                                </div>
                            </div>

                            {/* PRODUCT TAGS */}
                            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 14px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <label style={{ fontWeight: '700', fontSize: '0.82rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    🏷️ Product Tags <span style={{ fontWeight: '400', color: '#94a3b8', fontSize: '0.75rem' }}>(comma-separated e.g. Beverage, Wine, Glass Bottle, Craft Beer)</span>
                                </label>
                                <input 
                                    type="text" 
                                    value={Array.isArray(localProduct.tags) ? localProduct.tags.join(', ') : (localProduct.tags || '')} 
                                    onChange={e => {
                                        const raw = e.target.value;
                                        const parsed = raw.split(',').map(t => t.trim()).filter(Boolean);
                                        updateField('tags', parsed);
                                    }} 
                                    placeholder="e.g. Beverage, Craft Beer, Recyclable, 750ml"
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', background: '#ffffff', boxSizing: 'border-box' }} 
                                />
                                <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                    Product tags can be used in search filters and to automatically trigger Bottle Deposit fees or auxiliary services.
                                </span>
                            </div>

                            {/* PRODUCT PERMALINK / URL SLUG */}
                            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 14px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                                    <label style={{ fontWeight: '700', fontSize: '0.82rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <LuLink size={13} /> Permalink / Product URL Slug
                                    </label>
                                    <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                        Preview: <code style={{ color: '#2563eb', fontWeight: 'bold' }}>/product/{localProduct.slug || slugify(localProduct.name || 'product')}</code>
                                    </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <span style={{ background: '#e2e8f0', color: '#475569', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', fontFamily: 'monospace' }}>
                                        /product/
                                    </span>
                                    <input 
                                        type="text" 
                                        value={localProduct.slug !== undefined ? localProduct.slug : (localProduct.pageConfig?.slug || slugify(localProduct.name || ''))} 
                                        onChange={e => {
                                            const raw = e.target.value;
                                            updateField('slug', raw.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-_]/g, ''));
                                        }} 
                                        placeholder={slugify(localProduct.name || 'product-slug')}
                                        style={{ flex: 1, minWidth: '180px', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'monospace', background: '#ffffff', boxSizing: 'border-box' }} 
                                    />
                                    <button 
                                        type="button" 
                                        onClick={() => updateField('slug', slugify(localProduct.name || ''))}
                                        title="Auto-generate slug from product title"
                                        style={{ padding: '8px 12px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '600', color: '#475569', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    >
                                        Auto Slug
                                    </button>
                                </div>
                            </div>

                            {/* PRODUCT CARD ANCHOR ID */}
                            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', padding: '12px 14px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                                    <label style={{ fontWeight: '700', fontSize: '0.82rem', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        🎯 Product Card Anchor ID (DOM #id)
                                    </label>
                                    <span style={{ fontSize: '0.72rem', color: '#0284c7' }}>
                                        Target: <code style={{ fontWeight: 'bold' }}>#{localProduct.cardAnchorId || localProduct.anchorId || (localProduct.slug ? `product-${localProduct.slug}` : `product-${localProduct.id}`)}</code>
                                    </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '8px 12px', borderRadius: '6px', border: '1px solid #bae6fd', fontSize: '0.85rem', fontFamily: 'monospace', fontWeight: 'bold' }}>
                                        #
                                    </span>
                                    <input 
                                        type="text" 
                                        value={localProduct.cardAnchorId !== undefined ? localProduct.cardAnchorId : (localProduct.anchorId || '')} 
                                        onChange={e => {
                                            const clean = e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-_]/g, '');
                                            updateField('cardAnchorId', clean);
                                            updateField('anchorId', clean);
                                        }} 
                                        placeholder={localProduct.slug ? `product-${localProduct.slug}` : `product-${localProduct.id}`}
                                        style={{ flex: 1, minWidth: '180px', padding: '8px 12px', border: '1px solid #bae6fd', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'monospace', background: '#ffffff', boxSizing: 'border-box', color: '#0369a1', fontWeight: '600' }} 
                                    />
                                    <button 
                                        type="button" 
                                        onClick={() => {
                                            const defAnchor = localProduct.slug ? `product-${localProduct.slug}` : `product-${localProduct.id}`;
                                            updateField('cardAnchorId', defAnchor);
                                            updateField('anchorId', defAnchor);
                                        }}
                                        title="Use default slug-based anchor"
                                        style={{ padding: '8px 12px', background: '#ffffff', border: '1px solid #bae6fd', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '600', color: '#0369a1', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    >
                                        Use Default
                                    </button>
                                </div>
                                <p style={{ margin: 0, fontSize: '0.72rem', color: '#0284c7' }}>
                                    Used when linking a category, menu item, or button directly to this product card on the shop page (e.g. <code>/shop#your-id</code>).
                                </p>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                        Current Price ($) *
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={localProduct.price !== undefined ? localProduct.price : ''}
                                        onChange={e => updateField('price', e.target.value)}
                                        placeholder="29.99"
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.95rem', fontWeight: '700', boxSizing: 'border-box' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                        Regular Price ($)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={localProduct.regularPrice !== undefined ? localProduct.regularPrice : ''}
                                        onChange={e => updateField('regularPrice', e.target.value)}
                                        placeholder="39.99"
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                        Sale Price ($)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={localProduct.salePrice !== undefined && localProduct.salePrice !== null ? localProduct.salePrice : ''}
                                        onChange={e => updateField('salePrice', e.target.value)}
                                        placeholder="Leave blank if no sale"
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                        Stock Quantity
                                    </label>
                                    {((Array.isArray(localProduct.variations) && localProduct.variations.length > 0) || (Array.isArray(localProduct.pageConfig?.variations) && localProduct.pageConfig.variations.length > 0)) ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                type="number"
                                                disabled
                                                value={(localProduct.variations || localProduct.pageConfig?.variations || []).reduce((sum, v) => sum + (parseInt(v.stock, 10) || 0), 0)}
                                                style={{ width: '90px', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', background: '#f8fafc', color: '#64748b', boxSizing: 'border-box' }}
                                            />
                                            <span style={{ fontSize: '0.78rem', color: '#64748b', fontStyle: 'italic' }}>Managed per variation (see Variations tab)</span>
                                        </div>
                                    ) : (
                                        <input
                                            type="number"
                                            min="0"
                                            value={localProduct.stock !== undefined ? localProduct.stock : 1}
                                            onChange={e => updateField('stock', e.target.value)}
                                            placeholder="100"
                                            style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Inquiry Mode Toggle */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#fffbeb', border: '1px solid #fde68a', padding: '12px 16px', borderRadius: '8px' }}>
                                <input
                                    type="checkbox"
                                    id="inquiryOnlyCheckbox"
                                    checked={Boolean(localProduct.inquiryOnly)}
                                    onChange={e => updateField('inquiryOnly', e.target.checked)}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <label htmlFor="inquiryOnlyCheckbox" style={{ fontSize: '0.85rem', fontWeight: '700', color: '#92400e', cursor: 'pointer' }}>
                                    Inquiry Only Mode (Hide "Add to Cart" and show "Request Quote / Inquire" button)
                                </label>
                            </div>

                            {/* Bottle Deposit & Container Recycling (CRF) */}
                            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', padding: '16px', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '1.2rem' }}>🍾</span>
                                        <div>
                                            <span style={{ fontWeight: '800', fontSize: '0.88rem', color: '#0369a1' }}>
                                                Bottle Deposit & Container Recycling (CRF)
                                            </span>
                                            <div style={{ fontSize: '0.74rem', color: '#0284c7' }}>
                                                Enables statutory per-container recycling deposit on checkout for this product.
                                            </div>
                                        </div>
                                    </div>
                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: '700', color: '#0369a1', cursor: 'pointer', background: '#e0f2fe', padding: '6px 12px', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                                        <input
                                            type="checkbox"
                                            checked={Boolean(localProduct.hasBottleDeposit)}
                                            onChange={e => updateField('hasBottleDeposit', e.target.checked)}
                                        />
                                        Requires Bottle Deposit
                                    </label>
                                </div>

                                {Boolean(localProduct.hasBottleDeposit) && (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px solid #e0f2fe' }}>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: '700', color: '#475569', marginBottom: '4px' }}>
                                                Bottle / Can Count per Item Unit
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={localProduct.bottleCount !== undefined && localProduct.bottleCount !== null ? localProduct.bottleCount : 1}
                                                onChange={e => updateField('bottleCount', parseInt(e.target.value, 10) || 1)}
                                                placeholder="e.g. 1 (single) or 6 (6-pack)"
                                                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                                            />
                                            <span style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '2px', display: 'block' }}>
                                                e.g. Enter 6 for a 6-pack to multiply deposit fee
                                            </span>
                                        </div>

                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: '700', color: '#475569', marginBottom: '4px' }}>
                                                Deposit Rate per Bottle ($ CAD/USD)
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={localProduct.bottleDepositFee !== undefined && localProduct.bottleDepositFee !== null ? localProduct.bottleDepositFee : ''}
                                                onChange={e => updateField('bottleDepositFee', e.target.value)}
                                                placeholder="Store default ($0.10)"
                                                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                                            />
                                            <span style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '2px', display: 'block' }}>
                                                Leave blank to inherit store default ($0.10)
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* TAB 2: DESCRIPTIONS */}
                    {activeTab === 'descriptions' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                    Short Summary Description (Card preview)
                                </label>
                                <textarea
                                    rows={3}
                                    value={localProduct.shortDescription || ''}
                                    onChange={e => updateField('shortDescription', e.target.value)}
                                    placeholder="Brief overview shown on grid cards..."
                                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box', resize: 'vertical' }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                    Full Product Details & Specifications (Product Page body)
                                </label>
                                <textarea
                                    rows={8}
                                    value={localProduct.description || ''}
                                    onChange={e => updateField('description', e.target.value)}
                                    placeholder="Detailed product story, specifications, dimensions, materials, care instructions..."
                                    style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box', resize: 'vertical' }}
                                />
                            </div>
                        </div>
                    )}

                    {/* TAB 3: MEDIA & SPEC SHEET */}
                    {activeTab === 'media' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            {/* Images Section */}
                            <div>
                                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>
                                    Product Images & Media Gallery
                                </h4>

                                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                                    <input
                                        type="text"
                                        value={newImageUrl}
                                        onChange={e => setNewImageUrl(e.target.value)}
                                        placeholder="Paste image URL here..."
                                        style={{ flex: 1, minWidth: '220px', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddImageUrl}
                                        style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' }}
                                    >
                                        + Add URL
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setMediaLibraryTarget('product_gallery')}
                                        style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '8px 16px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                    >
                                        <LuImage size={14} /> Choose from Uploads
                                    </button>
                                    <label style={{ background: '#10b981', color: 'white', borderRadius: '6px', padding: '8px 16px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                        {uploadingImage ? 'Uploading...' : <><LuUpload size={14} /> Upload File</>}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            disabled={uploadingImage}
                                            onChange={handleImageFileUpload}
                                            style={{ display: 'none' }}
                                        />
                                    </label>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px' }}>
                                    {(localProduct.images || []).map((imgUrl, i) => (
                                        <div
                                            key={i}
                                            style={{
                                                border: i === 0 ? '2px solid #2563eb' : '1px solid #cbd5e1',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                position: 'relative',
                                                background: '#f8fafc',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                            }}
                                        >
                                            <div style={{ height: '110px', overflow: 'hidden' }}>
                                                <img
                                                    src={resolveImageUrl(imgUrl)}
                                                    alt={`Image ${i + 1}`}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                />
                                            </div>
                                            {i === 0 && (
                                                <div style={{ position: 'absolute', top: '4px', left: '4px', background: '#2563eb', color: 'white', fontSize: '0.65rem', fontWeight: '700', padding: '2px 6px', borderRadius: '4px' }}>
                                                    Primary
                                                </div>
                                            )}
                                            <div style={{ padding: '4px', display: 'flex', justifyContent: 'space-between', background: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
                                                {i > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSetPrimaryImage(i)}
                                                        title="Set as primary cover image"
                                                        style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: '0.72rem', cursor: 'pointer', fontWeight: '600' }}
                                                    >
                                                        ★ Make Primary
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveImage(i)}
                                                    title="Remove image"
                                                    style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.75rem', cursor: 'pointer', marginLeft: 'auto', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}
                                                >
                                                    <LuTrash2 size={13} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Spec Sheet & Custom Document Links Section */}
                            <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <h4 style={{ margin: '0', fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>
                                    📄 Spec Sheet & Custom Document Links
                                </h4>

                                {/* Primary Spec Sheet */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', background: '#fff', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                                    <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#475569' }}>Primary Spec Sheet / Technical Document</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                        <div>
                                            <label style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px' }}>Custom Button Label</label>
                                            <input
                                                type="text"
                                                value={localProduct.specSheetTitle || ''}
                                                onChange={e => updateField('specSheetTitle', e.target.value)}
                                                placeholder="Download Technical Spec Sheet (PDF)"
                                                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px' }}>Document / File URL</label>
                                            <input
                                                type="text"
                                                value={localProduct.specSheetUrl || ''}
                                                onChange={e => updateField('specSheetUrl', e.target.value)}
                                                placeholder="URL to downloadable Spec Sheet PDF..."
                                                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
                                        <button
                                            type="button"
                                            onClick={() => setMediaLibraryTarget('spec_sheet')}
                                            style={{ background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: '6px', padding: '6px 12px', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}
                                        >
                                            📄 Choose from Uploads
                                        </button>
                                        <label style={{ background: '#0284c7', color: 'white', borderRadius: '6px', padding: '6px 12px', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                            {uploadingSpecSheet ? 'Uploading PDF...' : '📄 Upload Spec Sheet'}
                                            <input
                                                type="file"
                                                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                                                disabled={uploadingSpecSheet}
                                                onChange={handleSpecSheetUpload}
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    </div>
                                </div>

                                {/* Additional Custom Links Builder */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <div style={{ fontSize: '0.82rem', fontWeight: 'bold', color: '#334155' }}>
                                        Additional Links & Attachments ({(Array.isArray(localProduct.customLinks) ? localProduct.customLinks : []).length})
                                    </div>
                                    {(Array.isArray(localProduct.customLinks) ? localProduct.customLinks : []).map((linkItem, idx) => (
                                        <div key={linkItem.id || idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', background: '#fff', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                                            <input
                                                type="text"
                                                value={linkItem.title || ''}
                                                onChange={e => {
                                                    const list = [...(localProduct.customLinks || [])];
                                                    list[idx] = { ...list[idx], title: e.target.value };
                                                    updateField('customLinks', list);
                                                }}
                                                placeholder="Custom Link Name (e.g. User Manual)"
                                                style={{ flex: 1, padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.82rem' }}
                                            />
                                            <input
                                                type="text"
                                                value={linkItem.url || ''}
                                                onChange={e => {
                                                    const list = [...(localProduct.customLinks || [])];
                                                    list[idx] = { ...list[idx], url: e.target.value };
                                                    updateField('customLinks', list);
                                                }}
                                                placeholder="URL (e.g. /uploads/manual.pdf or https://...)"
                                                style={{ flex: 1.5, padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.82rem' }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const list = (localProduct.customLinks || []).filter((_, i) => i !== idx);
                                                    updateField('customLinks', list);
                                                }}
                                                style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '4px', padding: '6px 10px', fontSize: '0.8rem', fontWeight: 'bold', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                                            >
                                                <LuX size={12} />
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const list = Array.isArray(localProduct.customLinks) ? [...localProduct.customLinks] : [];
                                            list.push({ id: 'link-' + Date.now(), title: '', url: '' });
                                            updateField('customLinks', list);
                                        }}
                                        style={{ alignSelf: 'flex-start', background: '#e0f2fe', color: '#0369a1', border: '1px solid #7dd3fc', borderRadius: '6px', padding: '6px 12px', fontSize: '0.8rem', fontWeight: 'bold', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                    >
                                        <LuPlus size={13} /> Add Custom Link
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB 4: ATTRIBUTES & VARIATIONS */}
                    {activeTab === 'variations' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            {/* Attributes Builder */}
                            <div style={{ background: '#f0f9ff', padding: '16px', borderRadius: '12px', border: '1px solid #bae6fd' }}>
                                <h4 style={{ margin: '0 0 6px 0', fontSize: '0.95rem', fontWeight: '700', color: '#0369a1' }}>
                                    1. Define Product Attributes (e.g. Size, Color, Finish)
                                </h4>
                                <p style={{ margin: '0 0 12px 0', fontSize: '0.78rem', color: '#0284c7' }}>
                                    Add options separated by commas (e.g. "Small, Medium, Large" or "Matte Black, Brushed Brass").
                                </p>

                                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                                    <input
                                        type="text"
                                        value={newAttrName}
                                        onChange={e => setNewAttrName(e.target.value)}
                                        placeholder="Attribute Name (e.g. Color)"
                                        style={{ width: '160px', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }}
                                    />
                                    <input
                                        type="text"
                                        value={newAttrOptions}
                                        onChange={e => setNewAttrOptions(e.target.value)}
                                        placeholder="Options (e.g. Brass, Black, Silver)"
                                        style={{ flex: 1, minWidth: '200px', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddAttribute}
                                        style={{ background: '#0284c7', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '0.85rem', fontWeight: 'bold', cursor: 'pointer' }}
                                    >
                                        + Add Attribute
                                    </button>
                                </div>

                                {Object.keys(localProduct.attributes || {}).length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {Object.entries(localProduct.attributes || {}).map(([attrName, options]) => (
                                            <div
                                                key={attrName}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    background: '#ffffff',
                                                    padding: '8px 12px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #cbd5e1'
                                                }}
                                            >
                                                <span style={{ fontWeight: '700', fontSize: '0.85rem', color: '#1e293b', minWidth: '110px' }}>
                                                    {attrName}:
                                                </span>
                                                <input
                                                    type="text"
                                                    defaultValue={Array.isArray(options) ? options.join(', ') : options}
                                                    onBlur={e => handleUpdateAttributeOptions(attrName, e.target.value)}
                                                    style={{ flex: 1, padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveAttribute(attrName)}
                                                    style={{ background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '4px', padding: '6px 10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                                                    title={`Remove ${attrName}`}
                                                >
                                                    <LuTrash2 size={12} /> Delete
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Variations Generator & Table */}
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                                    <div>
                                        <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>
                                            2. Manage Variations Matrix ({localProduct.variations?.length || 0} variations)
                                        </h4>
                                        <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                                            Customize prices, SKUs, and dedicated images per variation combination
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleGenerateVariations}
                                        style={{
                                            background: '#8b5cf6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '8px 16px',
                                            fontSize: '0.85rem',
                                            fontWeight: 'bold',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        ⚡ Generate Variations from Attributes
                                    </button>
                                </div>

                                {(!localProduct.variations || localProduct.variations.length === 0) ? (
                                    <div style={{ textAlign: 'center', padding: '24px', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '8px', color: '#64748b', fontSize: '0.85rem' }}>
                                        No variations created yet. Click "Generate Variations from Attributes" above to generate combinations.
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {localProduct.variations.map((v, vIdx) => {
                                            const varDesc = Object.entries(v.attributes || {}).map(([k, val]) => `${k}: ${val}`).join(' | ') || `Variation #${vIdx + 1}`;
                                            return (
                                                <div
                                                    key={v.id || vIdx}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '10px',
                                                        padding: '10px 14px',
                                                        background: '#f8fafc',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '8px',
                                                        flexWrap: 'wrap'
                                                    }}
                                                >
                                                    {/* Variation Image Thumbnail */}
                                                    <div style={{ width: '44px', height: '44px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #cbd5e1', flexShrink: 0, position: 'relative' }}>
                                                        <img
                                                            src={resolveImageUrl(v.image || (localProduct.images && localProduct.images[0]))}
                                                            alt={varDesc}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    </div>

                                                    <div style={{ flex: '1 1 180px' }}>
                                                        <div style={{ fontWeight: '700', fontSize: '0.85rem', color: '#0f172a' }}>{varDesc}</div>
                                                        <input
                                                            type="text"
                                                            value={v.sku || ''}
                                                            onChange={e => handleUpdateVariationField(v.id, 'sku', e.target.value)}
                                                            placeholder="SKU..."
                                                            style={{ width: '140px', padding: '3px 6px', fontSize: '0.75rem', border: '1px solid #cbd5e1', borderRadius: '4px', marginTop: '2px' }}
                                                        />
                                                    </div>

                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#475569' }}>
                                                            Price:
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={v.price !== undefined ? v.price : ''}
                                                                onChange={e => handleUpdateVariationField(v.id, 'price', parseFloat(e.target.value) || 0)}
                                                                style={{ width: '70px', padding: '4px 6px', marginLeft: '4px', fontSize: '0.8rem', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                                                            />
                                                        </label>
                                                        <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#475569' }}>
                                                            Stock:
                                                            <input
                                                                type="number"
                                                                value={v.stock !== undefined ? v.stock : 999}
                                                                onChange={e => handleUpdateVariationField(v.id, 'stock', parseInt(e.target.value, 10) || 0)}
                                                                style={{ width: '55px', padding: '4px 6px', marginLeft: '4px', fontSize: '0.8rem', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                                                            />
                                                        </label>
                                                    </div>

                                                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => setMediaLibraryTarget(`var_${v.id}`)}
                                                            style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '4px', padding: '4px 8px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                                                        >
                                                            <LuImage size={11} /> Library
                                                        </button>
                                                        <label style={{ background: '#e2e8f0', color: '#334155', borderRadius: '4px', padding: '4px 8px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                                            {variationImgUploadingId === v.id ? '...' : <><LuUpload size={11} /> Upload</>}
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                disabled={variationImgUploadingId === v.id}
                                                                onChange={e => {
                                                                    if (e.target.files && e.target.files[0]) {
                                                                        handleVariationImageUpload(v.id, e.target.files[0]);
                                                                    }
                                                                }}
                                                                style={{ display: 'none' }}
                                                            />
                                                        </label>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveVariation(v.id)}
                                                        style={{ background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                        title="Delete variation"
                                                    >
                                                        <LuTrash2 size={12} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal Footer Controls */}
                <div style={{
                    padding: '16px 24px',
                    backgroundColor: '#f8fafc',
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0
                }}>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            background: '#ffffff',
                            color: '#475569',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px'
                        }}
                    >
                        <LuX size={14} /> Discard / Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSaveAndApply}
                        style={{
                            padding: '10px 24px',
                            borderRadius: '8px',
                            border: 'none',
                            background: '#10b981',
                            color: '#ffffff',
                            fontWeight: '700',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3)'
                        }}
                    >
                        <LuSave size={16} />
                        <span>Apply & Done</span>
                    </button>
                </div>
            </div>

            {/* Reusable Media Library Modal */}
            <MediaLibraryModal
                isOpen={Boolean(mediaLibraryTarget)}
                onClose={() => setMediaLibraryTarget(null)}
                onSelect={handleMediaLibrarySelect}
                title={mediaLibraryTarget === 'spec_sheet' ? 'Select Spec Sheet from Uploads' : 'Select Product Image from Uploads'}
                allowedTypes={mediaLibraryTarget === 'spec_sheet' ? ['document', 'image', 'video'] : ['image', 'video']}
            />
        </div>
    );
};

export default ProductExpandedEditModal;
